// Practicar la brújula corporal (`body_compass_practice`, escena s02).
//
// Se muestran las tarjetas que declara el contenido, una a una, y para cada
// una las mismas tres respuestas: tranquilo, no sé, uh-oh. **Cualquier
// respuesta es válida** (`anyAnswerValid: true` en el contenido) y ninguna se
// guarda (`storeAnswers: false`): esto no evalúa a nadie, enseña a nombrar lo
// que se siente en el cuerpo.
//
// Por eso aquí no hay acierto ni error, ni contador, ni forma de "hacerlo
// mal" (Constitución V). Después de cada tarjeta responde Capi con la línea
// que el guion trae.
//
// Todo el texto —tarjetas, respuestas y respuesta de Capi— viene de
// `content/`; aquí no hay ni una palabra escrita para un niño. Tuvo un rótulo
// «Tu brújula» y la revisión de content-guardian lo bloqueó con razón: no
// nombraba una acción, nombraba el concepto central del episodio con el mismo
// posesivo que usa Capi, y salía en el sitio donde va quien habla.
//
// La cabecera del minijuego es la propia tarjeta. Cada tarjeta se responde una
// vez: el guion no contempla volver atrás, y como no se guarda ni se juzga
// nada, no hay nada que rehacer.

import { useState } from "react";

import type { BodyCompassPracticeConfig, CastId, LocId } from "../../engine";
import { obtenerArteDeTarjeta, UI_CARD_ASSETS } from "../../shared/assets";
import { RespuestaDeMinijuego } from "../dialogue/RespuestaDeMinijuego";
import { IlustracionDeTarjeta } from "../ui/IlustracionDeTarjeta";

interface BrujulaCorporalProps {
  readonly config: BodyCompassPracticeConfig;
  readonly texto: (locId: LocId) => string;
  readonly nombreDe: (castId: CastId) => string;
  readonly alTerminar: () => void;
}

export function BrujulaCorporal({
  config,
  texto,
  nombreDe,
  alTerminar,
}: BrujulaCorporalProps) {
  const [indice, setIndice] = useState(0);
  const [respondida, setRespondida] = useState(false);

  const tarjeta = config.cards[indice];
  if (!tarjeta) {
    // Sin tarjetas no hay nada que practicar; se continúa en vez de dejar la
    // pantalla en blanco.
    return null;
  }

  // El contenido cambia la respuesta de Capi a partir de cierta tarjeta.
  const respuesta =
    indice >= config.feedbackVariantAfterCard.cardIndexFrom
      ? config.feedbackVariantAfterCard
      : config.feedbackAfterEachCard;

  const esUltima = indice === config.cards.length - 1;
  const ilustracionEscenario = obtenerArteDeTarjeta(UI_CARD_ASSETS.scenarios, tarjeta.id);

  function continuar(): void {
    if (esUltima) {
      alTerminar();
      return;
    }
    setIndice((anterior) => anterior + 1);
    setRespondida(false);
  }

  if (respondida) {
    return (
      <RespuestaDeMinijuego
        speaker={respuesta.speaker}
        nombre={nombreDe(respuesta.speaker)}
        texto={texto(respuesta.locId)}
        contexto={texto(tarjeta.locId)}
        alContinuar={continuar}
      />
    );
  }

  return (
    <section className="minijuego minijuego--brujula" aria-labelledby="brujula-tarjeta">
      <div className="minijuego__contexto">
        <h2 className="dialogo__texto" id="brujula-tarjeta">
          {texto(tarjeta.locId)}
        </h2>
        {ilustracionEscenario ? (
          <IlustracionDeTarjeta
            src={ilustracionEscenario}
            className="opcion__ilustracion--compacta"
          />
        ) : null}
      </div>

      <ul className="decisiones__lista">
        {config.answers.map(({ id, locId, icon }, posicion) => {
          const ilustracion = obtenerArteDeTarjeta(UI_CARD_ASSETS.icons, icon);
          return (
            <li key={id}>
              <button
                type="button"
                className="objetivo-tactil boton boton--opcion"
                data-principal={posicion === 0 ? "true" : undefined}
                onClick={() => setRespondida(true)}
              >
                {ilustracion ? (
                  <IlustracionDeTarjeta src={ilustracion} className="opcion__ilustracion--compacta" />
                ) : null}
                <span>{texto(locId)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
